const STORAGE_KEY = "nowadays.commandPalette.recents";
const MAX_RECENTS = 4;

/**
 * Reads the recently-selected item ids.
 *
 * Every access is guarded: private windows, cleared site data and browsers set
 * to block storage all throw on `localStorage` rather than returning empty, and
 * the palette must still open when they do.
 *
 * @returns Item ids, most recent first. Empty when unavailable or unparseable.
 */
export function readRecentIds(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === "string").slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

/**
 * Records a selected item id as the most recent, de-duplicated.
 *
 * @param id - The selected item's id.
 */
export function pushRecentId(id: string): void {
  try {
    const next = [id, ...readRecentIds().filter((entry) => entry !== id)].slice(0, MAX_RECENTS);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage is a convenience here, never a correctness requirement.
  }
}

/** Prefix for a Recent row's key and cmdk value, so it cannot collide with the
 *  same item's row in its home group. */
export const RECENT_KEY_PREFIX = "recent:";

/**
 * Resolves stored ids to items, in stored order, dropping any that no longer
 * exist.
 *
 * An id can outlive its item: a route gets removed, or an event-scoped item is
 * unavailable because the current route names no event. Dropping silently is
 * right — a recent list is a convenience, and a dangling row that navigates
 * nowhere is worse than a shorter list.
 *
 * @param items - The items currently available in this context.
 * @param ids - Stored recent ids, most recent first.
 * @returns The matching items, most recent first.
 */
export function resolveRecents<T extends { id: string }>(items: T[], ids: string[]): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return ids.map((id) => byId.get(id)).filter((item): item is T => item !== undefined);
}
